<script setup>
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import EvolutionChannel from 'dashboard/api/channel/evolutionChannel';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useStore } from 'vuex';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const inboxName = ref('');
const phoneNumber = ref('');
const isCreating = ref(false);
const qrCodeBase64 = ref(null);
const instanceName = ref('');
const isConnected = ref(false);

const rules = {
  inboxName: { required },
  phoneNumber: { required },
};
const v$ = useVuelidate(rules, { inboxName, phoneNumber });

let activeIntervalId = null;
let isPolling = false;

const goBack = () => {
  if (activeIntervalId) {
    clearInterval(activeIntervalId);
    activeIntervalId = null;
  }
  router.push({
    name: 'settings_inbox_new',
    query: { provider: undefined },
  });
};

const cancelQrCode = () => {
  if (activeIntervalId) {
    clearInterval(activeIntervalId);
    activeIntervalId = null;
  }
  qrCodeBase64.value = null;
  isCreating.value = false;
};

const pollConnectionStatus = async () => {
  if (isPolling) return;
  isPolling = true;
  try {
    const response = await EvolutionChannel.checkStatus(instanceName.value);
    if (response.data.state === 'open') {
      if (isConnected.value) {
        isPolling = false;
        return;
      }
      isConnected.value = true;
      if (activeIntervalId) {
        clearInterval(activeIntervalId);
        activeIntervalId = null;
      }
      useAlert(this.$t('INBOX_ALERTS.EVOLUTION_CONNECTED_CREATING_INBOX'));

      try {
        const inboxResponse = await EvolutionChannel.createInbox({
          name: inboxName.value,
          phone_number: phoneNumber.value,
          instance_name: instanceName.value,
        });

        store.commit('inboxes/ADD_INBOXES', inboxResponse.data);

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            accountId: route.params.accountId,
            inbox_id: inboxResponse.data.id,
          },
          query: {
            page: 'new',
          },
        });
      } catch (inboxError) {
        const errorMessage =
          inboxError.response?.data?.error ||
          inboxError.message ||
          t('INBOX_ALERTS.EVOLUTION_CREATE_INBOX_ERROR');
        useAlert(errorMessage);
        isConnected.value = false;
        qrCodeBase64.value = null;
      }
    } else if (response.data.qrcode && response.data.qrcode !== '') {
      qrCodeBase64.value = response.data.qrcode;
    }
  } catch (error) {
    // wait for next poll
  } finally {
    isPolling = false;
  }
};

const createInstance = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR'));
    return;
  }

  if (isCreating.value) return;

  if (activeIntervalId) {
    clearInterval(activeIntervalId);
    activeIntervalId = null;
  }

  isCreating.value = true;
  try {
    const name = `${inboxName.value}-${Date.now().toString(36)}`;
    const response = await EvolutionChannel.createInstance({
      name: name,
      phone_number: phoneNumber.value,
    });
    qrCodeBase64.value = response.data.qrcode;
    instanceName.value = response.data.instanceName;

    activeIntervalId = setInterval(pollConnectionStatus, 3000);
  } catch (error) {
    useAlert(
      error.message || t('INBOX_ALERTS.EVOLUTION_CREATE_INSTANCE_ERROR')
    );
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div class="wizard-body w-full max-w-2xl mt-8">
    <PageHeader
      :title="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.TITLE')"
      :description="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.DESCRIPTION')"
    />

    <form
      v-if="!qrCodeBase64"
      class="mx-0 mt-8 mb-4 w-[65%] min-h-[400px] pb-32"
      @submit.prevent="createInstance"
    >
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="v$.inboxName.$model"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>

      <label class="mt-4" :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <PhoneNumberInput
          v-model="v$.phoneNumber.$model"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.REQUIRED') }}
        </span>
      </label>

      <div class="flex gap-2 justify-end mt-6">
        <NextButton
          variant="ghost"
          color="slate"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.CANCEL')"
          @click="goBack"
        />
        <NextButton
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.GENERATE_QR_CODE')"
          color="blue"
          :is-loading="isCreating"
          @click="createInstance"
        />
      </div>
    </form>

    <div v-else class="flex flex-col items-center mt-8">
      <h2 class="text-xl font-semibold mb-4">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SCAN_QR_CODE_TITLE') }}
      </h2>
      <p class="mb-6 text-gray-600">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.SCAN_QR_CODE_DESCRIPTION') }}
      </p>
      <div v-if="qrCodeBase64" class="p-4 border rounded-xl bg-white shadow-sm">
        <img
          :src="qrCodeBase64"
          :alt="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR_CODE_ALT')"
          class="w-64 h-64"
        />
      </div>
      <p v-if="!isConnected" class="mt-6 text-sm text-gray-500 animate-pulse">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.WAITING_FOR_CONNECTION') }}
      </p>
      <p v-else class="mt-6 text-sm text-green-600 font-bold">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CONNECTED') }}
      </p>

      <div class="mt-8 flex gap-2">
        <NextButton
          variant="ghost"
          color="slate"
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.CANCEL')"
          @click="cancelQrCode"
        />
      </div>
    </div>
  </div>
</template>
